import {
  Avatar,
  AvatarImage,
  InitialsAvatarFallback,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import { Separator } from "@workspace/ui/components/separator";
import { TextWithTooltip } from "@workspace/ui/components/text-with-tooltip";
import { cn } from "@workspace/ui/lib/utils";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, Building2, Clock, Heart, MapPin } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";
import { Icons } from "@/components/icons";
import { COMMITMENTS, WORK_MODELS } from "@/config/constants";
import { isMarketRateCompensation } from "@/lib/compensation";
import { formatCurrency } from "@/lib/utils";
import type { ApplicationListing } from "./types";

type ListingSidebarProps = {
  listing: ApplicationListing;
  applyButtonLabel: string;
  isFavourited: boolean;
  isFavouriteButtonPending: boolean;
  isStartingApplication: boolean;
  onApply: () => void;
  onToggleFavourite: (e: MouseEvent<HTMLButtonElement>) => void;
};

const RATE_PER_LABELS: Record<string, string> = {
  hourly: "per hour",
  daily: "per day",
  weekly: "per week",
  monthly: "per month",
  annually: "per year",
};

function SidebarFact({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="text-foreground text-xs">{children}</span>
    </div>
  );
}

export function ListingSidebar({
  listing,
  applyButtonLabel,
  isFavourited,
  isFavouriteButtonPending,
  isStartingApplication,
  onApply,
  onToggleFavourite,
}: ListingSidebarProps) {
  const { compensation } = listing;
  const isMarketRate = isMarketRateCompensation(compensation);
  const ratePerLabel = RATE_PER_LABELS[compensation.rate] ?? "";
  const commitmentLabel =
    COMMITMENTS.find((c) => c.value === listing.commitment)?.label ?? "";
  const workModelLabel =
    WORK_MODELS.find((w) => w.value === listing.work_model)?.label ?? "";

  const locationParts = [listing.address.city, listing.address.region].filter(
    Boolean
  );
  const locationText =
    locationParts.length > 0
      ? locationParts.join(", ")
      : (listing.address.street ?? "");

  return (
    <aside className="hidden lg:block">
      <Card className="sticky top-4 gap-0 rounded-lg py-0">
        <CardContent className="grid gap-4 p-4">
          <div>
            <p className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
              Compensation
            </p>
            <p className="mt-1 font-bold text-2xl text-foreground tracking-tight">
              {isMarketRate
                ? "Market rate"
                : formatCurrency(compensation.amount ?? 0)}
            </p>
            {!isMarketRate && (ratePerLabel || commitmentLabel) ? (
              <p className="text-muted-foreground text-xs">
                {[ratePerLabel, commitmentLabel]
                  .filter(Boolean)
                  .join(" \u00B7 ")}
              </p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Button
              className="w-full gap-1.5"
              disabled={isStartingApplication}
              onClick={onApply}
              type="button"
            >
              {isStartingApplication ? (
                <Icons.spinner className="size-4 animate-spin" />
              ) : null}
              {isStartingApplication ? "Starting..." : applyButtonLabel}
              {isStartingApplication ? null : (
                <ArrowRight className="size-3.5" />
              )}
            </Button>
            <Button
              className="w-full gap-1.5"
              disabled={isFavouriteButtonPending}
              onClick={onToggleFavourite}
              variant="outline"
            >
              {isFavouriteButtonPending ? (
                <Icons.spinner className="size-3.5 animate-spin" />
              ) : (
                <Heart
                  className={cn(
                    "size-3.5 transition",
                    isFavourited
                      ? "fill-destructive text-destructive"
                      : "fill-transparent"
                  )}
                />
              )}
              {isFavourited ? "Saved" : "Save for later"}
            </Button>
          </div>

          <Separator />

          <div className="grid gap-2.5">
            {locationText ? (
              <SidebarFact icon={MapPin}>{locationText}</SidebarFact>
            ) : null}
            {workModelLabel ? (
              <SidebarFact icon={Building2}>{workModelLabel}</SidebarFact>
            ) : null}
            {commitmentLabel ? (
              <SidebarFact icon={Clock}>{commitmentLabel}</SidebarFact>
            ) : null}
          </div>

          <Separator />

          <div className="flex min-w-0 items-center gap-2.5">
            <Avatar className="size-7 shrink-0 rounded-lg">
              <AvatarImage
                alt={listing.postedBy ?? ""}
                src={listing.postedByImage ?? undefined}
              />
              <InitialsAvatarFallback
                className="rounded-lg"
                name={listing.postedBy ?? ""}
              />
            </Avatar>
            <TextWithTooltip
              className="min-w-0 flex-1 font-semibold text-foreground text-xs"
              text={listing.postedBy ?? "Unknown"}
            />
          </div>
        </CardContent>
      </Card>
    </aside>
  );
}
